import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../../components/ui/Modal";
import { Stack } from "../../components/ui/Stack";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Alert } from "../../components/ui/Alert";
import { Spinner } from "../../components/ui/Spinner";
import { MultiSelect } from "../../components/ui/MultiSelect";
import Tabs, { Tab } from "../../components/Tabs";
import { useApi } from "../../context/ApiContext";
import {
  COUNTRIES,
  CURRENCIES,
  getCountryByCode,
  getCurrencyByCode,
} from "../../utils/countryCurrencyData";

interface LocationFormProps {
  isOpen: boolean;
  locationId?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const LocationForm: React.FC<LocationFormProps> = ({
  isOpen,
  locationId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { api } = useApi();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shippingMethods, setShippingMethods] = useState<any[]>([]);
  const [contributors, setContributors] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    country: "",
    countryCode: "",
    currency: "",
    currencySymbol: "",
    taxRate: 0,
    taxIncluded: false,
    taxFreeBusiness: false,
    taxId: "",
    shippingIds: [] as string[],
    storeOwnerIds: [] as string[],
    isActive: true,
    default: false,
    mappedCountryCodes: [] as string[],
    whatsappPhoneNumber: "",
    // Shipper/Sender address information
    shipperName1: "",
    shipperName2: "",
    shipperName3: "",
    shipperAddressStreet: "",
    shipperAddressHouse: "",
    shipperPostalCode: "",
    shipperCity: "",
  });

  useEffect(() => {
    if (isOpen) {
      fetchShippingMethods();
      fetchContributors();
      if (locationId) {
        fetchLocation();
      } else {
        // Reset form for new location
        setFormData({
          country: "",
          countryCode: "",
          currency: "",
          currencySymbol: "",
          taxRate: 0,
          taxIncluded: false,
          taxFreeBusiness: false,
          taxId: "",
          shippingIds: [],
          storeOwnerIds: [],
          isActive: true,
          default: false,
          mappedCountryCodes: [],
          whatsappPhoneNumber: "",
          shipperName1: "",
          shipperName2: "",
          shipperName3: "",
          shipperAddressStreet: "",
          shipperAddressHouse: "",
          shipperPostalCode: "",
          shipperCity: "",
        });
        setError(null);
        setActiveTab(0);
      }
    }
  }, [isOpen, locationId]);

  const fetchShippingMethods = async () => {
    try {
      const response = await api.get(`/shipping/admin`);
      setShippingMethods(response.data.methods || []);
    } catch (err: any) {
      console.error("Failed to fetch shipping methods:", err);
    }
  };

  const fetchContributors = async () => {
    try {
      const response = await api.get("/profit-rules/contributors");
      setContributors(response.data || []);
    } catch (err: any) {
      console.error("Failed to fetch contributors:", err);
    }
  };

  const fetchLocation = async () => {
    try {
      setFetching(true);
      const response = await api.get(`/locations/admin/${locationId}`);
      const location = response.data;
      if (location) {
        setFormData({
          country: location.country || "",
          countryCode: location.countryCode || "",
          currency: location.currency || "",
          currencySymbol: location.currencySymbol || "",
          taxRate: location.taxRate || 0,
          taxIncluded: location.taxIncluded || false,
          taxFreeBusiness: location.taxFreeBusiness || false,
          taxId: location.taxId || "",
          shippingIds:
            location.shippingIds?.map((id: any) => id.toString()) || [],
          storeOwnerIds:
            location.storeOwnerIds?.map((id: any) => id.toString()) || [],
          isActive: location.isActive !== undefined ? location.isActive : true,
          default: location.default || false,
          mappedCountryCodes: location.mappedCountryCodes || [],
          whatsappPhoneNumber: location.whatsappPhoneNumber || "",
          shipperName1: location.shipperName1 || "",
          shipperName2: location.shipperName2 || "",
          shipperName3: location.shipperName3 || "",
          shipperAddressStreet: location.shipperAddressStreet || "",
          shipperAddressHouse: location.shipperAddressHouse || "",
          shipperPostalCode: location.shipperPostalCode || "",
          shipperCity: location.shipperCity || "",
        });
      } else {
        setError(t("admin.locations.errors.notFound"));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.locations.errors.fetch"));
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // If tax-free business is enabled, set tax rate to 0
      const finalTaxRate = formData.taxFreeBusiness ? 0 : formData.taxRate;

      const payload = {
        ...formData,
        countryCode: formData.countryCode.toUpperCase(),
        currency: formData.currency.toUpperCase(),
        taxRate: finalTaxRate,
        shippingIds: formData.shippingIds,
        storeOwnerIds: formData.storeOwnerIds,
        mappedCountryCodes: formData.mappedCountryCodes.map((code) =>
          code.toUpperCase(),
        ),
      };

      if (locationId) {
        await api.put(`/locations/admin/${locationId}`, payload);
      } else {
        await api.post("/locations/admin", payload);
      }

      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.locations.errors.save"));
    } finally {
      setLoading(false);
    }
  };

  // Tab content components
  const generalTabContent = (
    <Stack spacing="md">
      {/* Country Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t("admin.locations.country")} <span className="text-red-500">*</span>
        </label>
        <select
          required
          value={formData.countryCode}
          onChange={(e) => {
            const countryCode = e.target.value;
            const country = getCountryByCode(countryCode);
            if (country) {
              setFormData({
                ...formData,
                countryCode: country.code,
                country: country.name,
                currency: country.currency,
                currencySymbol: country.currencySymbol,
              });
            }
          }}
          className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{t("admin.locations.selectCountry")}</option>
          {COUNTRIES.map((country) => (
            <option key={country.code} value={country.code}>
              {country.name} ({country.code})
            </option>
          ))}
        </select>
        {formData.countryCode && (
          <p className="mt-1 text-sm text-gray-500">
            {t("admin.locations.selected")}: {formData.country}
          </p>
        )}
      </div>

      {/* Currency Selectors */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("admin.locations.currencyIso")}{" "}
            <span className="text-red-500">*</span>
          </label>
          <select
            required
            value={formData.currency}
            onChange={(e) => {
              const currencyCode = e.target.value;
              const currency = getCurrencyByCode(currencyCode);
              if (currency) {
                setFormData({
                  ...formData,
                  currency: currency.code,
                  currencySymbol: currency.symbol,
                });
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t("admin.locations.selectCurrency")}</option>
            {CURRENCIES.map((currency) => (
              <option key={currency.code} value={currency.code}>
                {currency.code} - {currency.name} ({currency.symbol})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t("admin.locations.currencySymbol")}{" "}
            <span className="text-red-500">*</span>
          </label>
          <select
            required
            value={formData.currencySymbol}
            onChange={(e) => {
              setFormData({ ...formData, currencySymbol: e.target.value });
            }}
            className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {CURRENCIES.map((currency) => (
              <option key={currency.code} value={currency.symbol}>
                {currency.symbol} ({currency.code})
              </option>
            ))}
          </select>
        </div>
      </div>
    </Stack>
  );

  const taxTabContent = (
    <Stack spacing="md">
      <div>
        <label className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            checked={formData.taxFreeBusiness}
            onChange={(e) => {
              const isTaxFree = e.target.checked;
              setFormData({
                ...formData,
                taxFreeBusiness: isTaxFree,
                taxRate: isTaxFree ? 0 : formData.taxRate,
              });
            }}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            {t("admin.locations.taxFreeBusiness")}
          </span>
        </label>
        {formData.taxFreeBusiness && formData.countryCode === "DE" && (
          <p className="text-sm text-gray-600 mb-4 ml-6">
            {t("admin.locations.taxFreeBusinessNote")}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label={t("admin.locations.taxRate")}
          type="number"
          step="0.01"
          min="0"
          max="100"
          required={!formData.taxFreeBusiness}
          disabled={formData.taxFreeBusiness}
          value={formData.taxRate}
          onChange={(e) =>
            setFormData({
              ...formData,
              taxRate: parseFloat(e.target.value) || 0,
            })
          }
        />

        <div className="flex items-center pt-8">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={formData.taxIncluded}
              disabled={formData.taxFreeBusiness}
              onChange={(e) =>
                setFormData({ ...formData, taxIncluded: e.target.checked })
              }
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
            />
            <span className="text-sm font-medium text-gray-700">
              {t("admin.locations.taxIncluded")}
            </span>
          </label>
        </div>
      </div>

      <Input
        label={t("admin.locations.taxId")}
        type="text"
        placeholder={t("admin.locations.taxIdPlaceholder")}
        helperText={t("admin.locations.taxIdHelper")}
        value={formData.taxId}
        onChange={(e) => setFormData({ ...formData, taxId: e.target.value })}
      />
    </Stack>
  );

  const shippingTabContent = (
    <Stack spacing="md">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t("admin.locations.availableShipping")}
        </label>
        <div className="border border-gray-300 p-3 max-h-64 overflow-y-auto">
          {shippingMethods.length === 0 ? (
            <p className="text-sm text-gray-500">
              {t("admin.locations.noShippingMethods")}
            </p>
          ) : (
            <div className="space-y-2">
              {shippingMethods.map((method) => (
                <label
                  key={method._id}
                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                >
                  <input
                    type="checkbox"
                    checked={formData.shippingIds.includes(
                      typeof method._id === "string"
                        ? method._id
                        : method._id.toString(),
                    )}
                    onChange={(e) => {
                      const methodId =
                        typeof method._id === "string"
                          ? method._id
                          : method._id.toString();
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          shippingIds: [...formData.shippingIds, methodId],
                        });
                      } else {
                        setFormData({
                          ...formData,
                          shippingIds: formData.shippingIds.filter(
                            (id) => id !== methodId,
                          ),
                        });
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    {method.name} ({method.type}) - ${method.basePrice}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </Stack>
  );

  const storeOwnersTabContent = (
    <Stack spacing="md">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t("admin.locations.storeOwners")}
        </label>
        <div className="border border-gray-300 p-3 max-h-64 overflow-y-auto">
          {contributors.length === 0 ? (
            <p className="text-sm text-gray-500">
              {t("admin.locations.noContributors")}
            </p>
          ) : (
            <div className="space-y-2">
              {contributors.map((contributor) => (
                <label
                  key={contributor._id}
                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                >
                  <input
                    type="checkbox"
                    checked={formData.storeOwnerIds.includes(
                      typeof contributor._id === "string"
                        ? contributor._id
                        : contributor._id.toString(),
                    )}
                    onChange={(e) => {
                      const contributorId =
                        typeof contributor._id === "string"
                          ? contributor._id
                          : contributor._id.toString();
                      if (e.target.checked) {
                        setFormData({
                          ...formData,
                          storeOwnerIds: [
                            ...formData.storeOwnerIds,
                            contributorId,
                          ],
                        });
                      } else {
                        setFormData({
                          ...formData,
                          storeOwnerIds: formData.storeOwnerIds.filter(
                            (id) => id !== contributorId,
                          ),
                        });
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    {contributor.name}{" "}
                    {contributor.email && `(${contributor.email})`}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </Stack>
  );

  const settingsTabContent = (
    <Stack spacing="md">
      <div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) =>
              setFormData({ ...formData, isActive: e.target.checked })
            }
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            {t("admin.active")}
          </span>
        </label>
      </div>

      <div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.default}
            onChange={(e) =>
              setFormData({ ...formData, default: e.target.checked })
            }
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-gray-700">
            Default Location (for unmapped countries)
          </span>
        </label>
        <p className="text-sm text-gray-500 mt-1 ml-6">
          When enabled, this location will be used for users from countries that
          are not directly supported or mapped to another location.
        </p>
      </div>

      <div>
        <MultiSelect
          label="Mapped Country Codes"
          helperText="Select countries that should map to this location. Users from these countries will see this location's settings."
          options={COUNTRIES.filter(
            (country) => country.code !== formData.countryCode,
          ).map((country) => ({
            value: country.code,
            label: `${country.name} (${country.code})`,
          }))}
          value={formData.mappedCountryCodes}
          onChange={(codes) => {
            setFormData({ ...formData, mappedCountryCodes: codes });
          }}
          placeholder="Select countries to map..."
        />
      </div>

      <div>
        <Input
          label="WhatsApp Phone Number"
          type="tel"
          placeholder="+1234567890"
          helperText="Optional. International format with country code (e.g., +1234567890). This number will be used for WhatsApp contact on order pages."
          value={formData.whatsappPhoneNumber}
          onChange={(e) =>
            setFormData({ ...formData, whatsappPhoneNumber: e.target.value })
          }
        />
      </div>
    </Stack>
  );

  const addressTabContent = (
    <Stack spacing="md">
      <p className="text-sm text-gray-600 mb-4">
        {t("admin.locations.shipperAddressHelper") ||
          "This address will be used as the sender address when generating shipping labels. Required for carrier integrations."}
      </p>

      <Input
        label={t("admin.locations.shipperName1") || "Company/Primary Name"}
        placeholder="Company name or primary name"
        required
        value={formData.shipperName1}
        onChange={(e) =>
          setFormData({ ...formData, shipperName1: e.target.value })
        }
        helperText={
          t("admin.locations.shipperName1Helper") ||
          "The company name or primary name for shipping labels"
        }
      />

      <Input
        label={t("admin.locations.shipperName2") || "Additional Name Line 2"}
        placeholder="Optional"
        value={formData.shipperName2}
        onChange={(e) =>
          setFormData({ ...formData, shipperName2: e.target.value })
        }
      />

      <Input
        label={t("admin.locations.shipperName3") || "Additional Name Line 3"}
        placeholder="Optional"
        value={formData.shipperName3}
        onChange={(e) =>
          setFormData({ ...formData, shipperName3: e.target.value })
        }
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label={t("admin.locations.shipperStreet") || "Street Name"}
          placeholder="Main street"
          required
          value={formData.shipperAddressStreet}
          onChange={(e) =>
            setFormData({ ...formData, shipperAddressStreet: e.target.value })
          }
        />

        <Input
          label={t("admin.locations.shipperHouse") || "House Number"}
          placeholder="123"
          required
          value={formData.shipperAddressHouse}
          onChange={(e) =>
            setFormData({ ...formData, shipperAddressHouse: e.target.value })
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label={t("admin.locations.shipperPostalCode") || "Postal Code"}
          placeholder="12345"
          required
          value={formData.shipperPostalCode}
          onChange={(e) =>
            setFormData({ ...formData, shipperPostalCode: e.target.value })
          }
        />

        <Input
          label={t("admin.locations.shipperCity") || "City"}
          placeholder="Berlin"
          required
          value={formData.shipperCity}
          onChange={(e) =>
            setFormData({ ...formData, shipperCity: e.target.value })
          }
        />
      </div>
    </Stack>
  );

  const tabs: Tab[] = [
    {
      title: t("admin.locations.tabs.general") || "General",
      content: generalTabContent,
    },
    {
      title: t("admin.locations.tabs.tax") || "Tax",
      content: taxTabContent,
    },
    {
      title: t("admin.locations.tabs.shipping") || "Shipping",
      content: shippingTabContent,
    },
    {
      title: t("admin.locations.tabs.storeOwners") || "Store Owners",
      content: storeOwnersTabContent,
    },
    {
      title: t("admin.locations.tabs.settings") || "Settings",
      content: settingsTabContent,
    },
    {
      title: t("admin.locations.tabs.address") || "Address",
      content: addressTabContent,
    },
  ];

  const tabsComponent = (
    <Tabs
      tabs={tabs}
      tabsKey="location-form"
      renderHeadersOnly={true}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    />
  );

  const footerContent = (
    <div className="flex gap-2 justify-end">
      <Button
        type="button"
        variant="outline"
        onClick={onClose}
        disabled={loading}
      >
        {t("common.cancel")}
      </Button>
      <Button type="submit" form="location-form" loading={loading}>
        {locationId ? t("common.update") : t("common.create")}
      </Button>
    </div>
  );

  if (fetching) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          locationId
            ? t("admin.locations.editCountry")
            : t("admin.locations.newCountry")
        }
        size="lg"
      >
        <div className="flex justify-center items-center py-8">
          <Spinner size="lg" />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        locationId
          ? t("admin.locations.editCountry")
          : t("admin.locations.newCountry")
      }
      size="xl"
      tabs={tabsComponent}
      footer={footerContent}
      errors={error ? <Alert variant="error">{error}</Alert> : undefined}
    >
      <form id="location-form" onSubmit={handleSubmit}>
        {tabs[activeTab]?.content}
      </form>
    </Modal>
  );
};
